/**
 * Kurze Kommandos, mit denen der Besucher K4ia sofort unterbricht — auch wenn
 * sie kürzer sind als die Sekunde, die das Dazwischenreden sonst verlangt
 * (apps/web/src/lib/dazwischenreden.ts). Ein Husten soll sie nicht stoppen,
 * ein „Stopp“ schon.
 *
 * Bewusst **nicht** dabei: „ja“, „okay“, „mhm“, „nein“, „was“ — so bestätigt
 * man beim Zuhören, ohne unterbrechen zu wollen. „Danke“ auch nicht: Das
 * erfindet eine Spracherkennung gern in ein Geräusch hinein.
 */
export const STANDARD_STICHWOERTER = [
  // Aufhören
  'stopp',
  'stop',
  'halt',
  'reicht',
  'genug',
  'abbruch',
  'abbrechen',
  'aufhören',
  'hör auf',
  'ruhe',
  'psst',
  'pst',
  'sei still',
  'still',
  'pause',
  // Warten, selbst etwas sagen
  'warte',
  'wart mal',
  'moment',
  'sekunde',
  'kurz',
  'eine frage',
  'frage',
  'entschuldigung',
  'sorry',
  // Weiter, springen
  'weiter',
  'nächster',
  'nächste',
  'nächstes',
  'überspringen',
  'skip',
  'zurück',
  'vorheriger',
  // Nochmal
  'nochmal',
  'noch mal',
  'noch einmal',
  'wiederholen',
  'wie bitte',
  'lauter',
  'leiser',
  'langsamer',
  // Ihr Name, wie ihn eine Spracherkennung schreibt
  'k4ia',
  'kaia',
  'kaya',
  'kaja',
  // Englisch
  'wait',
  'enough',
  'cancel',
  'next',
  'hold on',
  'repeat',
  'again',
  'go back',
  'question',
] as const

/**
 * Was eine Spracherkennung in Stille oder Rauschen gern hineinhört — nie ein
 * Kommando, auch wenn ein Stichwort darin vorkommt („Bis zum nächsten Mal“).
 */
const ERFUNDENE_SAETZE = [
  'untertitel',
  'bis zum nächsten mal',
  'bis zum nächsten video',
  'vielen dank fürs zuschauen',
  'danke fürs zuschauen',
  'thanks for watching',
  'thank you for watching',
]

/** Mehr Wörter sind kein Kommando mehr, sondern ein Satz — dafür gilt die Sekunde. */
const HOECHSTENS_WOERTER = 6

/** Kleinbuchstaben, ohne Satzzeichen, Leerzeichen vereinheitlicht. Umlaute bleiben. */
export function normalisiereAussage(text: string): string {
  return text
    .toLowerCase()
    .replace(/ß/g, 'ss')
    .replace(/\([^)]*\)|\[[^\]]*\]/g, ' ') // „(Husten)“, „[Musik]“
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

/** Die Liste aus dem Admin: eine je Zeile oder durch Komma getrennt; leer = Vorgabe. */
export function parseStichwoerter(roh: string): string[] {
  const liste = roh
    .split(/[\n,;]/)
    .map((w) => normalisiereAussage(w))
    .filter(Boolean)
  return liste.length > 0 ? liste : STANDARD_STICHWOERTER.map((w) => normalisiereAussage(w))
}

/**
 * Das Stichwort, das in einer kurzen Aussage steckt, oder `null`. Verglichen
 * wird wortweise: „stopp“ trifft „Stopp, bitte!“, aber nicht „Stoppuhr“.
 */
export function findeStichwort(text: string, woerter: readonly string[]): string | null {
  const aussage = normalisiereAussage(text)
  if (!aussage) return null
  if (ERFUNDENE_SAETZE.some((s) => aussage.includes(normalisiereAussage(s)))) return null
  if (aussage.split(' ').length > HOECHSTENS_WOERTER) return null
  const mitRand = ` ${aussage} `
  for (const wort of woerter) {
    const w = normalisiereAussage(wort)
    if (w && mitRand.includes(` ${w} `)) return w
  }
  return null
}
